import React from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// AdminDashboardHeaderSkeleton — FASE 4
// Fuente de verdad: docs/admin-dashboard-skeleton-roadmap/fase-0-inventario-arquitectonico.md
// Regla crítica: solo skeleton-shimmer (shimmer direccional), nunca animate-pulse.
//
// Objetivo principal: PREVENIR CLS.
// El header real devuelve `null` si no tiene slots. Durante la carga, eso significa
// que el espacio del header desaparece y reaparece — causando un salto de layout.
// Este skeleton reserva exactamente el mismo espacio desde el primer render.
//
// Clases estructurales copiadas 1:1 de AdminDashboardHeader.tsx:
//   <header id="admin-dashboard-header"
//     className="z-40 border-b border-sidebar-border bg-sidebar px-4 py-3
//                shadow-[0_1px_0_0_hsl(var(--sidebar-border))]
//                md:sticky md:top-0 sm:px-6 md:px-8"
//     style={{ backgroundColor: 'hsl(var(--sidebar-background))' }}
//   >
//
// Alto resultante: py-3 (12px×2) + contenido (~36px) = ~60px — idéntico al real.
// =============================================================================

// ─── Primitivo base ──────────────────────────────────────────────────────────
function Block({ 
    className }: Readonly<{ className?: string }>) {
  return (
    <div
      aria-hidden="true"
      className={cn('skeleton-shimmer rounded', className)}
    />
  );
}

// =============================================================================
// Desktop layout — flex items-center justify-between gap-4
//   Left: headerDesktopSearch → min-w-0 flex-1 max-w-[680px]
//     Search bar: rounded-full h-9 (input pill con icono lupa)
//   Right: flex items-center justify-end gap-2 sm:gap-3
//     headerDesktopActions → h-9 w-24 rounded-full (notif/etc)
//     Divider: mx-1 hidden h-7 w-px sm:block (bg-sidebar-border)
//     headerUserMenu → avatar size-9 rounded-full
// =============================================================================
function DesktopHeaderSkeleton() {
  return (
    <div className="flex items-center justify-between gap-4">
      {/* Left: Search — min-w-0 flex-1 max-w-[680px] */}
      <div className="min-w-0 flex-1 max-w-[680px]">
        {/* Input pill: h-9 rounded-full, emula buscador de propiedades */}
        <Block className="h-9 w-full rounded-full" />
      </div>

      {/* Right: actions + divider + user menu */}
      <div className="flex items-center justify-end gap-2 sm:gap-3">
        {/* headerDesktopActions — 3 botones: Raíz, Refresh, Notificaciones (size-10) */}
        <Block className="size-10 rounded-full" />
        <Block className="size-10 rounded-full" />
        <Block className="size-10 rounded-full" />

        {/* Divider: mx-1 hidden h-7 w-px sm:block */}
        <div
          aria-hidden="true"
          className="mx-1 hidden h-7 w-px bg-sidebar-border sm:block"
        />

        {/* headerUserMenu — avatar: size-10 rounded-full */}
        <Block className="size-10 rounded-full" />
      </div>
    </div>
  );
}

// =============================================================================
// Mobile layout — flex items-center gap-2.5
//   [Burger button: size-10 rounded-full border border-sidebar-border]
//   [headerMobileSearch: min-w-0 flex-1 → input pill h-9]
//   [Right: actions + divider? + user avatar]
// =============================================================================
function MobileHeaderSkeleton() {
  return (
    <div className="flex items-center gap-2.5">
      {/* Burger button: size-10 rounded-full border border-sidebar-border */}
      <Block className="size-10 shrink-0 rounded-full" />

      {/* headerMobileSearch: min-w-0 flex-1 */}
      <div className="min-w-0 flex-1">
        <Block className="h-9 w-full rounded-full" />
      </div>

      {/* Right: actions + user menu */}
      <div className="flex items-center gap-1.5">
        {/* headerMobileActions — compact=true -> size-9 */}
        <Block className="size-9 rounded-full" />
        <Block className="size-9 rounded-full" />
        <Block className="size-9 rounded-full" />
        {/* Divider mx-0.5 h-6 w-px */}
        <div aria-hidden="true" className="mx-0.5 h-6 w-px bg-sidebar-border" />
        {/* headerUserMenu — avatar */}
        <Block className="size-9 rounded-full" />
      </div>
    </div>
  );
}

// =============================================================================
// EXPORT PÚBLICO — AdminDashboardHeaderSkeleton
//
// Props:
//   isMobile (boolean, default false):
//     Calca la prop `isMobile` del componente real para renderizar el layout correcto.
//
// Propiedades estructurales que previenen CLS:
//   - Mismo `id="admin-dashboard-header"` — referencia de layout compartida
//   - Mismo `z-40 border-b bg-sidebar px-4 py-3 md:sticky md:top-0 sm:px-6 md:px-8`
//   - Mismo `style={{ backgroundColor: 'hsl(var(--sidebar-background))' }}`
//   - Sin `return null` — siempre pinta el espacio reservado
// =============================================================================
export interface AdminDashboardHeaderSkeletonProps {
  /** Calca la prop isMobile del header real — determina qué layout se pinta. */
  isMobile?: boolean;
}

export function AdminDashboardHeaderSkeleton({ 
    isMobile = false }: Readonly<AdminDashboardHeaderSkeletonProps>) {
  return (
    <header
      id="admin-dashboard-header"
      className="z-40 border-b border-sidebar-border bg-sidebar px-4 py-3 shadow-[0_1px_0_0_hsl(var(--sidebar-border))] md:sticky md:top-0 sm:px-6 md:px-8"
      style={{ backgroundColor: 'hsl(var(--sidebar-background))' }}
      aria-label="Cargando cabecera del panel"
      aria-busy="true"
    >
      {isMobile ? <MobileHeaderSkeleton /> : <DesktopHeaderSkeleton />}
    </header>
  );
}
